/**
 * AABB + rotated-rectangle math. Objects rotate around their center, so the
 * world AABB of a rotated object is the min/max envelope of its rotated
 * corners — the shape culling and marquee selection test against.
 *
 * @module floorplan/utils/bounds
 * @category FloorPlan
 */

import type { Bounds, Vec2 } from '../models/geometry';
import type { FloorObject } from '../models/object';

const DEG_TO_RAD = Math.PI / 180;

/** Effective (scaled) size of an object. */
export const effectiveSize = (
  object: Pick<FloorObject, 'width' | 'height' | 'scale'>,
): { width: number; height: number } => {
  'worklet';

  return {
    width: object.width * object.scale,
    height: object.height * object.scale,
  };
};

/** Center point of an object's unrotated frame (rotation pivot). */
export const objectCenter = (
  object: Pick<FloorObject, 'x' | 'y' | 'width' | 'height' | 'scale'>,
): Vec2 => {
  'worklet';

  const size = effectiveSize({ ...object, scale: object.scale });
  return {
    x: object.x + size.width / 2,
    y: object.y + size.height / 2,
  };
};

/** Rotate `point` around `pivot` by `degrees` (clockwise, screen-aligned y-down). */
export const rotatePoint = (
  point: Vec2,
  pivot: Vec2,
  degrees: number,
): Vec2 => {
  'worklet';

  const rad = degrees * DEG_TO_RAD;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  };
};

/** The four corners of an object's frame, rotated into world space. */
export const objectCorners = (
  object: FloorObject,
): [Vec2, Vec2, Vec2, Vec2] => {
  const size = effectiveSize(object);
  const center = objectCenter(object);
  const corners: [Vec2, Vec2, Vec2, Vec2] = [
    { x: object.x, y: object.y },
    { x: object.x + size.width, y: object.y },
    { x: object.x + size.width, y: object.y + size.height },
    { x: object.x, y: object.y + size.height },
  ];
  if (object.rotation === 0) return corners;
  return [
    rotatePoint(corners[0], center, object.rotation),
    rotatePoint(corners[1], center, object.rotation),
    rotatePoint(corners[2], center, object.rotation),
    rotatePoint(corners[3], center, object.rotation),
  ];
};

/** World AABB of an object, accounting for rotation + scale. */
export const objectAabb = (object: FloorObject): Bounds => {
  const corners = objectCorners(object);
  let minX = corners[0].x;
  let minY = corners[0].y;
  let maxX = corners[0].x;
  let maxY = corners[0].y;
  for (let i = 1; i < corners.length; i += 1) {
    const corner = corners[i];
    if (corner === undefined) continue;
    if (corner.x < minX) minX = corner.x;
    if (corner.y < minY) minY = corner.y;
    if (corner.x > maxX) maxX = corner.x;
    if (corner.y > maxY) maxY = corner.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/** Do two AABBs intersect (touching edges count)? */
export const boundsIntersect = (a: Bounds, b: Bounds): boolean => {
  'worklet';

  if (a.x + a.width < b.x) return false;
  if (b.x + b.width < a.x) return false;
  if (a.y + a.height < b.y) return false;
  if (b.y + b.height < a.y) return false;
  return true;
};

/** Is `point` inside `bounds` (inclusive)? */
export const boundsContainPoint = (bounds: Bounds, point: Vec2): boolean => {
  'worklet';

  if (point.x < bounds.x) return false;
  if (point.y < bounds.y) return false;
  if (point.x > bounds.x + bounds.width) return false;
  if (point.y > bounds.y + bounds.height) return false;
  return true;
};

/** Smallest AABB containing both inputs. */
export const boundsUnion = (a: Bounds, b: Bounds): Bounds => {
  const minX = Math.min(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxX = Math.max(a.x + a.width, b.x + b.width);
  const maxY = Math.max(a.y + a.height, b.y + b.height);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/** Union AABB of many objects; null for an empty list. */
export const objectsAabb = (objects: readonly FloorObject[]): Bounds | null => {
  let result: Bounds | null = null;
  for (const object of objects) {
    const aabb = objectAabb(object);
    result = result === null ? aabb : boundsUnion(result, aabb);
  }
  return result;
};

/** Normalize a rect that may have negative width/height (marquee drags). */
export const normalizeBounds = (bounds: Bounds): Bounds => {
  'worklet';

  const x = bounds.width < 0 ? bounds.x + bounds.width : bounds.x;
  const y = bounds.height < 0 ? bounds.y + bounds.height : bounds.y;
  return {
    x,
    y,
    width: Math.abs(bounds.width),
    height: Math.abs(bounds.height),
  };
};
